import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { mergeStyleSets } from "@fluentui/react";
import { CaretRight, CaretLeft } from "@phosphor-icons/react";
import { boroondaraPalette } from "../theme/boroondaraTheme";
import { accessService } from "../services/accessService";

const p = boroondaraPalette;

interface CardDef {
  title: string;
  description: string;
  // Accent colour drives the trailing arrow tint. No icon — the title alone
  // identifies the card.
  color: string;
  route: string;
  tier: "public" | "governed";
  // Governed cards drill from category → tile. Public cards have no category.
  category?: "Finance" | "Assets" | "CRM" | "Administration" | "Information Technology" | "People & Workforce";
}

const cards: CardDef[] = [
  {
    title: "Council Dashboard",
    description: "Finance, assets, projects, service requests and workforce metrics",
    color: "#7C7CFF",
    route: "/dashboards/council",
    tier: "public",
  },
  {
    title: "Economic Activity",
    description: "Pedestrian activity, business registrations, labour market, retail trade, property and tourism insights",
    color: "#F59E0B",
    route: "/dashboards/economic",
    tier: "public",
  },
  {
    title: "Finance & Contract Expenditure",
    description: "Budget vs actuals, cost centres, project spend and purchase orders from Oracle Fusion",
    color: "#2e873d",
    route: "/dashboards/finance",
    tier: "governed",
    category: "Finance",
  },
  {
    title: "Assets & Infrastructure",
    description: "Asset register, condition, work orders, defects and customer requests from Conquest",
    color: "#059669",
    route: "/dashboards/assets",
    tier: "governed",
    category: "Assets",
  },
  {
    title: "CRM Request Analytics",
    description: "Oracle Service Cloud request volumes, SLA compliance, channel breakdown and department performance",
    color: "#1D4ED8",
    route: "/dashboards/crm",
    tier: "governed",
    category: "CRM",
  },
  {
    title: "Customer Survey Analytics",
    description: "Post-Resolution Survey satisfaction, ease of service, resolution rates, trends and verbatim feedback",
    color: "#0D9488",
    route: "/dashboards/survey",
    tier: "governed",
    category: "CRM",
  },
  {
    title: "Data Lineage & Pipeline Health",
    description: "Bronze → silver → gold pipeline health, run logs, failure root causes and dataset lineage",
    color: "#475569",
    route: "/dashboards/lineage",
    tier: "governed",
    category: "Administration",
  },
  {
    title: "Call Summary by Department",
    description: "Microsoft Teams external call volumes by directorate, department, officer and extension — inbound/outbound, answered vs abandoned",
    color: "#4F46E5",
    route: "/dashboards/it-call-summary",
    tier: "governed",
    category: "Information Technology",
  },
  {
    title: "Call Search",
    description: "Teams calling — per-call detail search with routing chain (caller → queue → answering officer) for IT troubleshooting",
    color: "#7C3AED",
    route: "/dashboards/it-call-search",
    tier: "governed",
    category: "Information Technology",
  },
  {
    title: "People Insights",
    description: "Workforce demographics, new starters, departures and turnover, leave balances and leave taken from PayGlobal",
    color: "#06B6D4",
    route: "/dashboards/people-insights",
    tier: "governed",
    category: "People & Workforce",
  },
];

// Governed-tier category drill-down. Click a category card to reveal the
// dashboards inside it. Real-Time Operations is being moved to its own
// top-nav tab; until then it stays accessible at /dashboards/realtime
// but is no longer surfaced on this hub.
type CategoryKey = "Finance" | "Assets" | "CRM" | "Administration" | "Information Technology" | "People & Workforce";
interface CategoryDef {
  key: CategoryKey;
  title: string;
  description: string;
  color: string;
}

const GOVERNED_CATEGORIES: CategoryDef[] = [
  {
    key: "Finance",
    title: "Finance",
    description: "Budgets, cost centres, project spend and purchase orders",
    color: "#2e873d",
  },
  {
    key: "Assets",
    title: "Assets",
    description: "Asset register, condition, work orders, defects and requests",
    color: "#059669",
  },
  {
    key: "CRM",
    title: "CRM",
    description: "Oracle Service Cloud request analytics and SLA performance",
    color: "#1D4ED8",
  },
  {
    key: "Administration",
    title: "Administration",
    description: "Platform internals — pipeline health, data lineage and framework quality",
    color: "#475569",
  },
  {
    key: "Information Technology",
    title: "Information Technology",
    description: "Telephony and IT service analytics — Teams calling by directorate and department",
    color: "#4F46E5",
  },
  {
    key: "People & Workforce",
    title: "People & Workforce",
    description: "Workforce demographics, starters and departures, leave balances and leave taken",
    color: "#06B6D4",
  },
];

const styles = mergeStyleSets({
  page: {
    padding: 32,
    minHeight: "calc(100vh - 52px)",
    background: "#F5F5F5",
    "@media (max-width: 768px)": { padding: 16 },
  } as any,
  container: { maxWidth: 1240, margin: "0 auto" },
  headerRow: { marginBottom: 32 },
  title: {
    color: p.text,
    fontWeight: 800 as any,
    fontSize: 28,
    letterSpacing: "-0.02em",
  },
  subtitle: {
    color: p.text2,
    marginTop: 6,
    maxWidth: 720,
    lineHeight: "1.6",
    fontSize: 14,
  },
  tabRow: {
    display: "flex",
    gap: 4,
    marginBottom: 24,
  },
  tab: {
    padding: "8px 18px",
    borderRadius: 4,
    fontSize: 13,
    fontWeight: 600 as any,
    border: "1px solid #EDEDED",
    background: "#FFFFFF",
    color: "#484848",
    cursor: "pointer",
    transition: "all 0.15s",
  },
  tabActive: {
    background: "#d9f4d7",
    borderColor: "rgba(46,135,61,0.4)",
    color: "#2e873d",
  },
  grid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
    gap: 20,
    "@media (max-width: 768px)": { gridTemplateColumns: "1fr" },
  } as any,
  card: {
    background: "#FFFFFF",
    borderRadius: 10,
    padding: "28px 24px",
    border: "1px solid #E1E1E1",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    cursor: "pointer",
    transition: "all 0.15s ease",
    display: "flex",
    flexDirection: "column",
    gap: 14,
    selectors: {
      ":hover": {
        boxShadow: "0 4px 16px rgba(0,0,0,0.10)",
        transform: "translateY(-2px)",
        borderColor: "#C4C9D0",
      },
    },
  } as any,
  cardTitle: {
    fontSize: 17,
    fontWeight: 700 as any,
    color: p.text,
  },
  cardDesc: {
    fontSize: 13,
    color: p.text3,
    lineHeight: "1.5",
  },
  cardArrow: {
    marginTop: "auto",
    fontSize: 12,
    fontWeight: 600 as any,
    display: "flex",
    alignItems: "center",
    gap: 4,
  },
  comingSoon: {
    padding: 56,
    textAlign: "center" as const,
    color: p.text3,
    fontSize: 14,
    lineHeight: "1.6",
    background: "#FFFFFF",
    borderRadius: 10,
    border: "1px solid #EDEDED",
  },
  comingSoonTitle: {
    color: p.text,
    fontWeight: 700 as any,
    fontSize: 18,
    marginBottom: 8,
  },
  backRow: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginBottom: 16,
    color: p.text2,
    fontSize: 13,
  },
  backLink: {
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    color: "#2e873d",
    fontWeight: 600 as any,
    background: "none",
    border: "none",
    padding: 0,
    fontSize: 13,
  },
  accessDenied: {
    padding: 56,
    textAlign: "center" as const,
    color: p.text3,
    fontSize: 14,
    lineHeight: "1.6",
    background: "#FFFFFF",
    borderRadius: 10,
    border: "1px solid #EDEDED",
  },
});

type DashboardTab = "public" | "governed";

export default function DashboardHub() {
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState<DashboardTab>("public");
  const [hasGovernedAccess, setHasGovernedAccess] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState<CategoryKey | null>(null);

  // Switching to public clears any drill-down so coming back to Governed
  // starts fresh at the category cards.
  const switchTab = (next: DashboardTab) => {
    setActiveTab(next);
    if (next === "public") setSelectedCategory(null);
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const res = await accessService.get<{
          role?: string;
          hasGovernedAccess?: boolean;
          // Kept for back-compat — older /me responses pre Phase 4c lacked
          // hasGovernedAccess; fall back to admin role + allowedAgents check.
          allowedAgents?: string[];
        }>("/me");
        if (!alive) return;
        const d = res.data;
        if (typeof d.hasGovernedAccess === "boolean") {
          setHasGovernedAccess(d.hasGovernedAccess);
        } else if (d.role === "admin") {
          setHasGovernedAccess(true);
        } else {
          setHasGovernedAccess((d.allowedAgents || []).length > 0);
        }
      } catch {
        // default to no governed access
      }
    })();
    return () => { alive = false; };
  }, []);

  const filtered = cards.filter((c) => c.tier === activeTab);

  return (
    <div className={styles.page}>
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <div className={styles.title}>Dashboards</div>
          <div className={styles.subtitle}>
            Explore council data through interactive dashboards with real-time KPIs and charts.
          </div>
        </div>

        {/* Tier toggle */}
        <div className={styles.tabRow}>
          <button
            className={`${styles.tab} ${activeTab === "public" ? styles.tabActive : ""}`}
            onClick={() => switchTab("public")}
          >
            Public
          </button>
          <button
            className={`${styles.tab} ${activeTab === "governed" ? styles.tabActive : ""}`}
            onClick={() => switchTab("governed")}
          >
            Governed
          </button>
        </div>

        {/* Public tab */}
        {activeTab === "public" && (
          filtered.length > 0 ? (
            <div className={styles.grid}>
              {filtered.map((card) => (
                <div
                  key={card.route}
                  className={styles.card}
                  onClick={() => nav(card.route)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => e.key === "Enter" && nav(card.route)}
                >
                  <div className={styles.cardTitle}>{card.title}</div>
                  <div className={styles.cardDesc}>{card.description}</div>
                  <div className={styles.cardArrow} style={{ color: card.color }}>
                    Open dashboard <CaretRight size={10} weight="bold" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={styles.comingSoon}>
              <div className={styles.comingSoonTitle}>No public dashboards</div>
              <div>Public dashboards will appear here.</div>
            </div>
          )
        )}

        {/* Governed tab — drill-down: Categories → Tiles within category. */}
        {activeTab === "governed" && (
          hasGovernedAccess ? (
            selectedCategory ? (
              // Tile view (after picking a category)
              <>
                <div className={styles.backRow}>
                  <button
                    type="button"
                    className={styles.backLink}
                    onClick={() => setSelectedCategory(null)}
                  >
                    <CaretLeft size={11} weight="bold" /> Categories
                  </button>
                  <span style={{ color: "#A0A0A0" }}>/</span>
                  <span style={{ color: p.text, fontWeight: 600 }}>{selectedCategory}</span>
                </div>
                <div className={styles.grid}>
                  {filtered
                    .filter((c) => c.category === selectedCategory)
                    .map((card) => (
                      <div
                        key={card.route}
                        className={styles.card}
                        onClick={() => nav(card.route)}
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => e.key === "Enter" && nav(card.route)}
                      >
                        <div className={styles.cardTitle}>{card.title}</div>
                        <div className={styles.cardDesc}>{card.description}</div>
                        <div className={styles.cardArrow} style={{ color: card.color }}>
                          Open dashboard <CaretRight size={10} weight="bold" />
                        </div>
                      </div>
                    ))}
                </div>
              </>
            ) : (
              // Category view (default — pick Finance or Assets)
              <div className={styles.grid}>
                {GOVERNED_CATEGORIES.map((cat) => (
                  <div
                    key={cat.key}
                    className={styles.card}
                    onClick={() => setSelectedCategory(cat.key)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Enter" && setSelectedCategory(cat.key)}
                  >
                    <div className={styles.cardTitle}>{cat.title}</div>
                    <div className={styles.cardDesc}>{cat.description}</div>
                    <div className={styles.cardArrow} style={{ color: cat.color }}>
                      View dashboards <CaretRight size={10} weight="bold" />
                    </div>
                  </div>
                ))}
              </div>
            )
          ) : (
            <div className={styles.accessDenied}>
              <div className={styles.comingSoonTitle}>Access Required</div>
              <div>
                Governed dashboards require enterprise access. Contact your administrator to request
                access to governed data.
              </div>
            </div>
          )
        )}
      </div>
    </div>
  );
}
